<template>
  <div class="grid gap-3">

    <!-- Switch Chat Type -->
    <div class="col-12 flex justify-content-center mb-3">
      <div class="flex gap-3">
       <Button 
  label="Segreteria"
  class="chat-switch-button"
  :class="{ active: selectedChatType === 'segreteria' }"
  @click="fetchChats('segreteria')"
/>

<Button 
  label="Responsabile"
  class="chat-switch-button"
  :class="{ active: selectedChatType === 'responsabile' }"
  @click="fetchChats('responsabile')"
/>
      </div>
    </div>

    <!-- Lista Chat -->
    <div class="col-12 md:col-4">
      <div class="card h-full shadow-2 p-3 rounded-xl">
        <h5 class="mb-3">Chat</h5>

        <!-- Search -->
        <div class="p-input-icon-left w-full mb-3">
          <i class="pi pi-search" />
          <InputText 
            v-model="searchQuery" 
            placeholder="Cerca utente..." 
            class="w-full" 
          />
        </div>

        <!-- Lista -->
        <div class="chat-list">
          <div v-if="loadingChats" class="flex justify-content-center py-5">
            <ProgressSpinner />
          </div>

          <div v-else-if="filteredChats.length === 0" class="text-center p-3 text-500">
            Nessuna chat trovata
          </div>

          <div 
            v-else 
            v-for="chat in filteredChats"
            :key="chat.id"
            class="chat-item flex align-items-center p-3 mb-2 rounded-lg cursor-pointer surface-card border-1 surface-border hover:surface-100 transition-all"
            :class="{'border-primary border-2 bg-primary-50': selectedChat && selectedChat.id === chat.id}"
            @click="selectChat(chat)"
          >
            <Avatar icon="pi pi-user" class="mr-3" shape="circle" />

            <div class="flex-1">
              <div class="flex justify-content-between mb-1">
                <span class="font-bold">
                  {{ chat.user ? chat.user.nome + ' ' + chat.user.cognome : 'Utente ' + chat.user_id }}
                </span>
                <span class="text-xs text-500">
                  {{ formatDate(chat.last_message_at) }}
                </span>
              </div>

              <div class="text-sm text-600 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                {{ chat.messages[0] ? chat.messages[0].message : 'Nessun messaggio' }}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Area Messaggi -->
    <div class="col-12 md:col-8">
      <div v-if="selectedChat" class="card h-full p-3 flex flex-column shadow-2 rounded-xl">

        <!-- Header Chat -->
        <div class="flex align-items-center border-bottom-1 surface-border pb-3 mb-3 gap-3">
          <Avatar icon="pi pi-user" size="large" shape="circle" />

          <div class="flex flex-column flex-1">
            <div class="font-bold text-xl">
              <a :href="'utente/'+selectedChat.user_id+'/dettagli'">
                {{ selectedChat.user ? selectedChat.user.nome+' '+selectedChat.user.cognome : 'Utente ' + selectedChat.user_id }}
              </a>
            </div>
            <div class="text-sm text-500">Destinatario: {{ selectedChat.type }}</div>

            <div class="mt-2 text-sm">
              <strong>Corsi:</strong>
              <div v-for="corso in selectedChat.corsi" :key="corso.id">
                <a :href="'corso/'+corso.id+'/dettagli'">{{ corso.nome }}</a>
              </div>
            </div>
          </div>

          <div class="text-right text-sm">
            <strong>Atleti:</strong>
            <div v-for="atleta in selectedChat.atleti" :key="atleta.id">
              <a :href="'atleta/'+atleta.id+'/dettagli'">{{ atleta.nome+' '+atleta.cognome }}</a>
            </div>
          </div>
        </div>

        <!-- Messaggi -->
        <div class="flex-1 overflow-y-auto mb-3 px-2 chat-messages" ref="messagesContainer">

          <div v-if="loadingMessages" class="flex justify-content-center py-5">
            <ProgressSpinner />
          </div>

          <div 
            v-else 
            v-for="msg in messages"
            :key="msg.id"
            class="mb-3 flex flex-column"
            :class="{'align-items-end': isMe(msg), 'align-items-start': !isMe(msg)}"
          >
            <div 
              class="p-3 rounded-xl max-w-20rem shadow-1"
              :class="isMe(msg) ? 'bg-primary text-white' : 'surface-100 text-900'"
            >
              {{ msg.message }}
            </div>
            <span class="text-xs text-500 mt-1">
              {{ formatDate(msg.created_at) }}
            </span>
          </div>

        </div>

        <!-- Input -->
        <div class="flex gap-2">
          <InputText 
            v-model="newMessage" 
            placeholder="Scrivi un messaggio..." 
            class="flex-1"
            @keyup.enter="sendMessage" 
          />
          <Button icon="pi pi-send" @click="sendMessage" :disabled="!newMessage.trim()" />
        </div>

      </div>

      <!-- Nessuna chat selezionata -->
      <div v-else class="card h-full flex align-items-center justify-content-center shadow-2 rounded-xl">
        <div class="text-center text-500">
          <i class="pi pi-comments text-6xl mb-3"></i>
          <div>Seleziona una chat per iniziare</div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance, computed } from 'vue';
import Service from '@/services/levante-api/levante-api';
import { useToast } from 'primevue/usetoast';
import moment from 'moment';

export default {
  setup() {
    const self = getCurrentInstance();
    const eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(eventBus);
    const toast = useToast();

    const chats = ref([]);
    const loadingChats = ref(false);
    const searchQuery = ref('');
    const selectedChat = ref(null);
    const selectedChatType = ref(null);
    const messages = ref([]);
    const loadingMessages = ref(false);
    const newMessage = ref('');
    const currentUser = ref(null);
    const messagesContainer = ref(null);

    const filteredChats = computed(() => {
      if (!searchQuery.value) return chats.value;
      const query = searchQuery.value.toLowerCase();
      return chats.value.filter(chat => {
        const name = chat.user ? chat.user.nome.toLowerCase() : '';
        return name.includes(query);
      });
    });

    const fetchChats = async (type) => {
      loadingChats.value = true;
      selectedChatType.value = type;
      console.log('Fetching chats...');
      try {
        const response = await service.getChats(type);
        chats.value = response.items.data;
        selectedChat.value = null;
      } catch (error) {
        console.error("Error fetching chats:", error);
      } finally {
        loadingChats.value = false;
      }
    };

    const getCurrentUserData = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) currentUser.value = user;
    };

    const selectChat = async (chat) => {
      selectedChat.value = chat;
      loadingMessages.value = true;
      messages.value = [];
      try {
        const response = await service.getChatMessages(chat.id);
        messages.value = response.items.data;
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        loadingMessages.value = false;
      }
    };

    const sendMessage = async () => {
      if (!newMessage.value.trim() || !selectedChat.value) return;

      const text = newMessage.value;
      newMessage.value = '';

      // Optimistic update
      messages.value.push({
        id: Date.now(),
        message: text,
        sender_id: 0,
        created_at: new Date().toISOString(),
        is_read: false
      });
      
      setTimeout(() => {
        scrollToBottom();
      }, 100);

      try {
        const response = await service.sendChatMessage(
          selectedChat.value.type,
          text,
          selectedChat.value.category_id,
          selectedChat.value.id
        );
        console.log(response);
        if (!response.item.data.success) {
          toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile inviare il messaggio',
            life: 3000
          });
        }
      } catch (error) {
        console.error("Error sending message:", error);
      }
    };

    const isMe = (msg) => {
        return msg.sender_id === 0;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
      }
    };

    onMounted(() => {
      fetchChats("segreteria");
      getCurrentUserData();
    });

    return {
      chats,
      loadingChats,
      searchQuery,
      selectedChatType,
      selectedChat,
      messages,
      loadingMessages,
      newMessage,
      currentUser,
      messagesContainer,
      filteredChats,
      fetchChats,
      selectChat,
      sendMessage,
      isMe,
      formatDate,
      scrollToBottom
    };
  }
};
</script>

<style scoped>
.chat-list {
    height: calc(100vh - 330px);
    overflow-y: auto;
}

.chat-item {
    transition: background 0.2s, border-color 0.2s;
}

.chat-messages {
    scroll-behavior: smooth;
}

.max-w-20rem {
    max-width: 22rem;
}
.chat-switch-button {
    background: var(--surface-100);
    border: 1px solid var(--surface-300);
    color: var(--text-color-secondary);
    transition: 0.2s;
    padding: 0.6rem 1.5rem;
    border-radius: 8px;
}

.chat-switch-button:hover {
    background: var(--surface-200);
}

.chat-switch-button.active {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: #fff;
}


</style>
